import { db } from '../config/firebase';
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';
import { DispositionLedgerEntry, AllCstResultsEntry } from '../types/pmt3Types';

// =============================================
// LEGACY COMPATIBILITY TYPES AND FUNCTIONS
// =============================================

// Legacy compatibility for old farming results data (from Client Bundle SKUs spreadsheet)
// This maintains compatibility with existing pmt2Service.ts imports
export interface FarmingResultsData {
  id: string;
  companyName: string;
  lastUpdated: Date;
  source: 'sheets';
  [key: string]: any; // Allow for dynamic fields from the old farming results sheet
}

// Legacy function to get farming results data (for backward compatibility)
export const getFarmingResultsData = async (companyName: string, asin: string): Promise<FarmingResultsData | null> => {
  try {
    // This function maintains compatibility with the old farming results from Client Bundle SKUs spreadsheet
    // Fetch from the old pmt2_farmingresults collection if it exists
    const q = query(
      collection(db, 'pmt2_farmingresults'),
      where('companyName', '==', companyName),
      where('id', '==', asin),
      limit(1)
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
      lastUpdated: doc.data().lastUpdated?.toDate() || new Date()
    } as FarmingResultsData;
  } catch (error) {
    console.error('Error fetching legacy farming results data:', error);
    return null;
  }
};

// Collection names
const DISPOSITION_LEDGER_COLLECTION = 'pmt3_disposition_ledger';
const ALL_CST_RESULTS_COLLECTION = 'pmt3_all_cst_results';

/**
 * Service for managing Farming Results data from Disposition Ledger and All CST Results sheets
 */

// =============================================
// DISPOSITION LEDGER OPERATIONS
// =============================================

/**
 * Gets all disposition ledger entries for a specific company
 */
export const getDispositionLedgerEntries = async (
  companyName: string,
  limitCount?: number
): Promise<DispositionLedgerEntry[]> => {
  try {
    let q = query(
      collection(db, DISPOSITION_LEDGER_COLLECTION),
      where('companyName', '==', companyName),
      orderBy('lastUpdated', 'desc')
    );

    if (limitCount) {
      q = query(q, limit(limitCount));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      lastUpdated: doc.data().lastUpdated?.toDate() || new Date()
    } as DispositionLedgerEntry));
  } catch (error) {
    console.error('Error fetching disposition ledger entries:', error);
    throw error;
  }
};

/**
 * Adds or updates disposition ledger entries using batch operations
 */
export const upsertDispositionLedgerEntries = async (
  entries: Omit<DispositionLedgerEntry, 'id'>[]
): Promise<void> => {
  try {
    const batch = writeBatch(db);
    const collectionRef = collection(db, DISPOSITION_LEDGER_COLLECTION);

    // First, get existing entries for each company to check for duplicates
    const companiesMap = new Map<string, Map<string, string>>(); // companyName -> uniqueCode -> docId

    for (const entry of entries) {
      if (!companiesMap.has(entry.companyName)) {
        const existingQuery = query(
          collectionRef,
          where('companyName', '==', entry.companyName)
        );
        const existingSnapshot = await getDocs(existingQuery);
        
        const companyEntries = new Map<string, string>();
        existingSnapshot.docs.forEach(doc => {
          const data = doc.data();
          companyEntries.set(data.uniqueCode, doc.id);
        });
        
        companiesMap.set(entry.companyName, companyEntries);
      }
    }

    // Process each entry
    for (const entry of entries) {
      const companyEntries = companiesMap.get(entry.companyName);
      const existingDocId = companyEntries?.get(entry.uniqueCode);

      const entryData = {
        ...entry,
        lastUpdated: serverTimestamp(),
        source: 'sheets' as const
      };

      if (existingDocId) {
        // Update existing entry
        const docRef = doc(db, DISPOSITION_LEDGER_COLLECTION, existingDocId);
        batch.update(docRef, entryData);
      } else {
        // Create new entry
        const docRef = doc(collectionRef);
        batch.set(docRef, entryData);
        // Update local map for subsequent entries
        companyEntries?.set(entry.uniqueCode, docRef.id);
      }
    }

    await batch.commit();
    console.log(`Successfully upserted ${entries.length} disposition ledger entries`);
  } catch (error) {
    console.error('Error upserting disposition ledger entries:', error);
    throw error;
  }
};

/**
 * Deletes all disposition ledger entries for a specific company
 */
export const deleteDispositionLedgerEntriesForCompany = async (
  companyName: string
): Promise<number> => {
  try {
    const q = query(
      collection(db, DISPOSITION_LEDGER_COLLECTION),
      where('companyName', '==', companyName)
    );

    const snapshot = await getDocs(q);
    const batch = writeBatch(db);

    snapshot.docs.forEach(docSnapshot => {
      batch.delete(docSnapshot.ref);
    });

    await batch.commit();
    console.log(`Deleted ${snapshot.docs.length} disposition ledger entries for ${companyName}`);
    return snapshot.docs.length;
  } catch (error) {
    console.error('Error deleting disposition ledger entries:', error);
    throw error;
  }
};

// =============================================
// ALL CST RESULTS OPERATIONS
// =============================================

/**
 * Gets all CST results entries for a specific company
 */
export const getAllCstResultsEntries = async (
  companyName: string,
  limitCount?: number
): Promise<AllCstResultsEntry[]> => {
  try {
    let q = query(
      collection(db, ALL_CST_RESULTS_COLLECTION),
      where('companyName', '==', companyName),
      orderBy('lastUpdated', 'desc')
    );

    if (limitCount) {
      q = query(q, limit(limitCount));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      lastUpdated: doc.data().lastUpdated?.toDate() || new Date()
    } as AllCstResultsEntry));
  } catch (error) {
    console.error('Error fetching all CST results entries:', error);
    throw error;
  }
};

/**
 * Adds or updates all CST results entries using batch operations
 */
export const upsertAllCstResultsEntries = async (
  entries: Omit<AllCstResultsEntry, 'id'>[]
): Promise<void> => {
  try {
    const batch = writeBatch(db);
    const collectionRef = collection(db, ALL_CST_RESULTS_COLLECTION);

    // First, get existing entries for each company to check for duplicates
    const companiesMap = new Map<string, Map<string, string>>(); // companyName -> uniqueCode2 -> docId

    for (const entry of entries) {
      if (!companiesMap.has(entry.companyName)) {
        const existingQuery = query(
          collectionRef,
          where('companyName', '==', entry.companyName)
        );
        const existingSnapshot = await getDocs(existingQuery);
        
        const companyEntries = new Map<string, string>();
        existingSnapshot.docs.forEach(doc => {
          const data = doc.data();
          companyEntries.set(data.uniqueCode2, doc.id);
        });
        
        companiesMap.set(entry.companyName, companyEntries);
      }
    }

    // Process each entry
    for (const entry of entries) {
      const companyEntries = companiesMap.get(entry.companyName);
      const existingDocId = companyEntries?.get(entry.uniqueCode2);

      const entryData = {
        ...entry,
        lastUpdated: serverTimestamp(),
        source: 'sheets' as const
      };

      if (existingDocId) {
        // Update existing entry
        const docRef = doc(db, ALL_CST_RESULTS_COLLECTION, existingDocId);
        batch.update(docRef, entryData);
      } else {
        // Create new entry
        const docRef = doc(collectionRef);
        batch.set(docRef, entryData);
        // Update local map for subsequent entries
        companyEntries?.set(entry.uniqueCode2, docRef.id);
      }
    }

    await batch.commit();
    console.log(`Successfully upserted ${entries.length} all CST results entries`);
  } catch (error) {
    console.error('Error upserting all CST results entries:', error);
    throw error;
  }
};

/**
 * Deletes all CST results entries for a specific company
 */
export const deleteAllCstResultsEntriesForCompany = async (
  companyName: string
): Promise<number> => {
  try {
    const q = query(
      collection(db, ALL_CST_RESULTS_COLLECTION),
      where('companyName', '==', companyName)
    );

    const snapshot = await getDocs(q);
    const batch = writeBatch(db);

    snapshot.docs.forEach(docSnapshot => {
      batch.delete(docSnapshot.ref);
    });

    await batch.commit();
    console.log(`Deleted ${snapshot.docs.length} all CST results entries for ${companyName}`);
    return snapshot.docs.length;
  } catch (error) {
    console.error('Error deleting all CST results entries:', error);
    throw error;
  }
};

// =============================================
// COMBINED OPERATIONS
// =============================================

/**
 * Gets farming results statistics for a company
 */
export const getFarmingResultsStats = async (companyName: string): Promise<{
  dispositionLedgerCount: number;
  allCstResultsCount: number;
  lastDispositionLedgerUpdate: Date | null;
  lastAllCstResultsUpdate: Date | null;
}> => {
  try {
    const [dispositionEntries, cstEntries] = await Promise.all([
      getDispositionLedgerEntries(companyName, 1),
      getAllCstResultsEntries(companyName, 1)
    ]);

    // Get counts
    const [dispositionCountQuery, cstCountQuery] = await Promise.all([
      getDocs(query(collection(db, DISPOSITION_LEDGER_COLLECTION), where('companyName', '==', companyName))),
      getDocs(query(collection(db, ALL_CST_RESULTS_COLLECTION), where('companyName', '==', companyName)))
    ]);

    return {
      dispositionLedgerCount: dispositionCountQuery.docs.length,
      allCstResultsCount: cstCountQuery.docs.length,
      lastDispositionLedgerUpdate: dispositionEntries[0]?.lastUpdated || null,
      lastAllCstResultsUpdate: cstEntries[0]?.lastUpdated || null
    };
  } catch (error) {
    console.error('Error getting farming results stats:', error);
    throw error;
  }
};

/**
 * Deletes all farming results data for a specific company
 */
export const deleteAllFarmingResultsForCompany = async (
  companyName: string
): Promise<{ dispositionDeleted: number; cstDeleted: number }> => {
  try {
    const [dispositionDeleted, cstDeleted] = await Promise.all([
      deleteDispositionLedgerEntriesForCompany(companyName),
      deleteAllCstResultsEntriesForCompany(companyName)
    ]);

    return { dispositionDeleted, cstDeleted };
  } catch (error) {
    console.error('Error deleting all farming results for company:', error);
    throw error;
  }
};

// Export the service object
export const pmt3FarmingResultsService = {
  // Disposition Ledger
  getDispositionLedgerEntries,
  upsertDispositionLedgerEntries,
  deleteDispositionLedgerEntriesForCompany,
  
  // All CST Results
  getAllCstResultsEntries,
  upsertAllCstResultsEntries,
  deleteAllCstResultsEntriesForCompany,
  
  // Combined
  getFarmingResultsStats,
  deleteAllFarmingResultsForCompany
};

export default pmt3FarmingResultsService;